import type React from 'react';
import type { AppController } from '@/engine/app/App.controller.ts';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import { useInjection } from 'inversify-react';
import { TYPES } from '@/engine/ioc';
import { ReactComponent as HeartSvg } from '@/assets/images/anatomical-heart.svg';
import { ReactComponent as LungSvg } from '@/assets/images/lungs.svg';

export const LoadingOverlay: React.FC = observer(() => {
  const appController = useInjection<AppController>(TYPES.AppController);

  switch (true) {
    case !appController.surgeryType:
      return (
        <LoadingContainer
          data-testid={'app-loading-' + appController.isLoadingOverlayActive}
          $isLoading={appController.isLoadingOverlayActive}
        >
          <div>
            Loading
            <span className={'dots'}>
              <span>.</span>
              <span>.</span>
              <span>.</span>
            </span>
          </div>
        </LoadingContainer>
      );
    // TODO: [namazov] Surgery specific loading animations should be moved to surgery specific components
    case appController.surgeryType === 'LUNG':
      return (
        <SurgeryLoadingContainer $isLoading={appController.isLoadingOverlayActive} $animation={'breath'}>
          <LungSvg width="16em" className={'animation'} />
        </SurgeryLoadingContainer>
      );
    case appController.surgeryType === 'TTVR' || appController.surgeryType === 'TAVR':
      return (
        <SurgeryLoadingContainer $isLoading={appController.isLoadingOverlayActive} $animation={'beat'}>
          <HeartSvg className={'animation'} />
        </SurgeryLoadingContainer>
      );
  }
});

const SurgeryLoadingContainer = styled.div<{ $isLoading: boolean; $animation: string }>`
  .animation {
    position: absolute;
    margin: auto;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    animation-name: ${(props): string => props.$animation};
    animation-duration: 1.2s;
    animation-iteration-count: infinite;
    opacity: ${(props): string => (props.$isLoading ? '1' : '0')};
    transition: 0.5s ease-in-out;
  }

  @keyframes beat {
    0% {
      transform: scale(0.8);
    }
    50% {
      transform: scale(1);
    }
    100% {
      transform: scale(0.8);
    }
  }

  @keyframes breath {
    0% {
      transform: scale(1);
    }
    50% {
      transform: scale(0.7);
    }
  }
`;

const LoadingContainer = styled.div<{ $isLoading: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  opacity: ${(props): string => (props.$isLoading ? '1' : '0')};
  position: absolute;
  width: 100%;
  height: 100%;
  transition: 0.2s ease-in-out;

  @keyframes dots-1 {
    from {
      opacity: 0;
    }
    25% {
      opacity: 1;
    }
  }
  @keyframes dots-2 {
    from {
      opacity: 0;
    }
    50% {
      opacity: 1;
    }
  }
  @keyframes dots-3 {
    from {
      opacity: 0;
    }
    75% {
      opacity: 1;
    }
  }
  @-webkit-keyframes dots-1 {
    from {
      opacity: 0;
    }
    25% {
      opacity: 1;
    }
  }
  @-webkit-keyframes dots-2 {
    from {
      opacity: 0;
    }
    50% {
      opacity: 1;
    }
  }
  @-webkit-keyframes dots-3 {
    from {
      opacity: 0;
    }
    75% {
      opacity: 1;
    }
  }

  .dots span {
    animation: dots-1 1s infinite steps(1);
    -webkit-animation: dots-1 1s infinite steps(1);
  }

  .dots span:first-child + span {
    animation-name: dots-2;
    -webkit-animation-name: dots-2;
  }

  .dots span:first-child + span + span {
    animation-name: dots-3;
    -webkit-animation-name: dots-3;
  }
`;
